import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Analytics from '../../services/analytics';
// mixpanel import removed since it's not used directly
// import mixpanel from 'mixpanel-browser';

interface SignupFormModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SignupFormModal: React.FC<SignupFormModalProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      setIsSubmitting(false);
      return;
    }

    // Track signup with Mixpanel
    Analytics.trackButtonClick('Signup Form Submit', 'modal');
    Analytics.trackSignup(email, 'modal');

    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      
      // Close modal after success message is shown
      setTimeout(() => {
        onClose();
        setIsSuccess(false);
        setEmail('');
      }, 5000);
    }, 1000);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="relative bg-white rounded-xl shadow-xl overflow-hidden w-full max-w-md"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 25 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute top-4 right-4">
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-8">
              {!isSuccess ? (
                <>
                  <div className="text-center mb-6">
                    <motion.div
                      className="h-12 w-12 rounded-lg bg-gradient-to-br from-primary-500 to-secondary-500 mx-auto mb-4"
                      initial={{ rotate: 0 }}
                      animate={{ rotate: 10 }}
                      transition={{ 
                        type: "spring", 
                        stiffness: 100, 
                        delay: 0.2 
                      }}
                    />
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Join AgentSpot</h2>
                    <p className="text-gray-600">
                      Sign up to get started with your free trial. No credit card required.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email address
                      </label>
                      <input
                        type="email"
                        id="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                        required
                      />
                      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
                    </div>

                    <motion.button
                      type="submit"
                      className="w-full btn btn-primary py-2 px-4 rounded-lg"
                      disabled={isSubmitting}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing...
                        </span>
                      ) : (
                        'Get Started'
                      )}
                    </motion.button>
                  </form>

                  <p className="mt-4 text-sm text-gray-500 text-center">
                    By signing up, you agree to our{' '}
                    <a href="/terms" className="text-primary-600 hover:text-primary-800">
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href="/privacy" className="text-primary-600 hover:text-primary-800">
                      Privacy Policy
                    </a>
                  </p>
                </>
              ) : (
                <div className="text-center py-6">
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 200 }}
                    className="mx-auto mb-4 h-16 w-16 rounded-full bg-green-100 flex items-center justify-center"
                  >
                    <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </motion.div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">You're in!</h2>
                  <p className="text-gray-600">
                  <br />
                  You've been added to the AgentSpot beta waiting list!
                  <br />
                  <br />
                  Due to high demand, your invitation is coming soon.
                  <br />
                  <br />
                  <b>Stay tuned!</b>
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SignupFormModal;